/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import {
  Breadcrumbs, Container, Link as BCLink, Box, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Chip, Switch, FormControlLabel, Tooltip,
  IconButton, Alert,
} from "@mui/material";
import { NavigateNext, Add, Edit, Delete, Send, CheckCircle, Cancel } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "../../axios";
import Auth from "../../Auth";
import { DataContext } from "../../App";
import usePermissionGuard from "../../hooks/usePermissionGuard";

const ALL_PERMISSIONS = [
  { key: "view_dashboard",        label: "Dashboard" },
  { key: "view_orders",           label: "Orders" },
  { key: "view_accounts",         label: "Accounts" },
  { key: "view_invoices",         label: "Invoices" },
  { key: "view_failed_orders",    label: "Failed Orders" },
  { key: "view_sync_status",      label: "Sync Status" },
  { key: "view_logs",             label: "Logs" },
  { key: "manage_settings",       label: "Settings" },
  { key: "manage_users",          label: "Manage Users" },
  { key: "impersonate_customers", label: "Impersonate Customers" },
];

const EMPTY_FORM = {
  first_name: "", last_name: "", email: "", password: "", permissions: [],
};

function PermissionToggle({ perm, checked, onChange, disabled }) {
  return (
    <FormControlLabel
      key={perm.key}
      control={
        <Switch size="small" checked={checked} onChange={e => onChange(perm.key, e.target.checked)}
          disabled={disabled} />
      }
      label={<span style={{ fontSize: 13 }}>{perm.label}</span>}
      sx={{ mr: 2, mb: 0.5 }}
    />
  );
}

function MemberCard({ member, onEdit, onDelete, canManage, isOwner }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "flex-start", justifyContent: "space-between",
      px: 3, py: 2.5, borderBottom: "1px solid #f0f0f0",
      "&:last-child": { borderBottom: "none" },
      "&:hover": { background: "#fafafa" },
    }}>
      {/* Avatar + name */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 200 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
          background: isOwner ? "#1a6b9a" : "#e8f0fe",
          color: isOwner ? "#fff" : "#1a6b9a",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 700, fontSize: 15,
        }}>
          {(member.first_name?.[0] ?? "?").toUpperCase()}
        </Box>
        <Box>
          <Box sx={{ fontWeight: 600, fontSize: 14, color: "#1a1a1a" }}>
            {member.first_name} {member.last_name}
            {isOwner && <Chip label="Owner" size="small" sx={{ ml: 1, background: "#e3f2fd", color: "#1565c0", fontSize: 11, fontWeight: 600, height: 20 }} />}
          </Box>
          <Box sx={{ fontSize: 12, color: "#888", mt: 0.25 }}>{member.email}</Box>
        </Box>
      </Box>

      {/* Status */}
      <Box sx={{ minWidth: 110, pt: 0.5 }}>
        {isOwner ? null : member.is_active
          ? <Chip icon={<CheckCircle sx={{ fontSize: 13 }} />} label="Active" size="small"
              sx={{ background: "#e8f5e9", color: "#2e7d32", fontSize: 11 }} />
          : <Chip icon={<Cancel sx={{ fontSize: 13 }} />} label="Pending" size="small"
              sx={{ background: "#fff8e1", color: "#e65100", fontSize: 11 }} />
        }
      </Box>

      {/* Permissions */}
      <Box sx={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 0.75, pt: 0.25 }}>
        {isOwner
          ? <Box sx={{ fontSize: 12, color: "#888", fontStyle: "italic" }}>All permissions</Box>
          : (member.permissions ?? []).length === 0
            ? <Box sx={{ fontSize: 12, color: "#bbb" }}>No permissions</Box>
            : (member.permissions ?? []).map(p => {
                const label = ALL_PERMISSIONS.find(x => x.key === p)?.label ?? p;
                return <Chip key={p} label={label} size="small"
                  sx={{ fontSize: 11, background: "#f0f4ff", color: "#1a6b9a", height: 22 }} />;
              })
        }
      </Box>

      {/* Added + actions */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2, flexShrink: 0 }}>
        {!isOwner && <Box sx={{ fontSize: 12, color: "#aaa", textAlign: "right", mr: 1 }}>{member.created_at}</Box>}
        {canManage && !isOwner && (
          <>
            <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(member)}><Edit sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            <Tooltip title="Remove"><IconButton size="small" color="error" onClick={() => onDelete(member)}><Delete sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}

export default function TeamMembers() {
  usePermissionGuard("manage_users");
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAuth } = useContext(DataContext);
  const canManage = Auth.hasPermission("manage_users");

  const [loading, setLoading]   = useState(true);
  const [members, setMembers]   = useState([]);
  const [owner, setOwner]       = useState(null);
  const [error, setError]       = useState(null);
  const [success, setSuccess]   = useState(null);

  // Dialog state
  const [dialog, setDialog]     = useState(null); // null | 'create' | 'invite' | 'edit'
  const [form, setForm]         = useState(EMPTY_FORM);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get("/admin/team")
      .then(r => { setMembers(r.data.members); setOwner(r.data.owner); })
      .catch(() => setError("Failed to load team members."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY_FORM); setFormError(null); setDialog("create"); };
  const openInvite = () => { setForm(EMPTY_FORM); setFormError(null); setDialog("invite"); };
  const openEdit   = (m) => {
    setEditTarget(m);
    setForm({ first_name: m.first_name, last_name: m.last_name, email: m.email,
      password: "", permissions: [...(m.permissions ?? [])], is_active: m.is_active });
    setFormError(null);
    setDialog("edit");
  };
  const closeDialog = () => { setDialog(null); setEditTarget(null); setFormError(null); };

  const togglePerm = (key, checked) => {
    setForm(f => ({
      ...f,
      permissions: checked ? [...f.permissions, key] : f.permissions.filter(p => p !== key),
    }));
  };

  const handleSave = () => {
    setSaving(true);
    setFormError(null);
    const endpoint = dialog === "edit"
      ? axios.patch(`/admin/team/${editTarget.id}`, form)
      : dialog === "invite"
        ? axios.post("/admin/team/invite", form)
        : axios.post("/admin/team", form);

    endpoint
      .then(r => {
        // If the current user edited their own permissions, update localStorage
        // and the React context so the nav reflects the change immediately.
        if (dialog === "edit" && r.data?.member) {
          const currentUser = Auth.getUser();
          if (currentUser && currentUser.id === editTarget.id) {
            const updated = { ...currentUser, permissions: r.data.member.permissions ?? [] };
            Auth.setUser(updated);
            setAuth({ currentUser: updated });
          }
        }
        setSuccess(dialog === "invite" ? "Invite sent successfully." : "Saved successfully.");
        closeDialog();
        load();
      })
      .catch(e => setFormError(e.response?.data?.message || Object.values(e.response?.data?.errors ?? {}).flat().join(" ")))
      .finally(() => setSaving(false));
  };

  const handleDelete = () => {
    axios.delete(`/admin/team/${deleteTarget.id}`)
      .then(() => { setSuccess("Member removed."); setDeleteTarget(null); load(); })
      .catch(() => setError("Failed to remove member."));
  };

  return (
    <div>
      <Container sx={{ pb: 4 }}>
        {/* Breadcrumbs */}
        <div role="presentation">
          <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ p: "14px 0" }}>
            <BCLink underline="none" color="inherit" onClick={() => navigate("/store/dashboard")}
              sx={{ cursor: "pointer" }} fontSize="14px">Dashboard</BCLink>
            <BCLink underline="none" color="text.primary" fontSize="14px">Team Members</BCLink>
          </Breadcrumbs>
        </div>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Team Members</h2>
            <div style={{ fontSize: 13, color: "#888", marginTop: 4 }}>
              Manage who has access to your store portal
            </div>
          </div>
          {canManage && (
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="outlined" startIcon={<Send />} onClick={openInvite} size="small">
                Invite by Email
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={openCreate} size="small"
                sx={{ background: "#1a6b9a" }}>
                Add Member
              </Button>
            </div>
          )}
        </div>

        {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}
        {error   && <Alert severity="error"   onClose={() => setError(null)}   sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ background: "#fff", border: "1px solid #ebebeb", borderRadius: 2, overflow: "hidden" }}>
            {owner && <MemberCard member={owner} onEdit={openEdit} onDelete={setDeleteTarget} canManage={canManage} isOwner />}
            {members.length === 0
              ? <Box sx={{ py: 6, textAlign: "center", color: "#bbb", fontSize: 14 }}>
                  No team members yet. Add or invite someone to get started.
                </Box>
              : members.map(m => (
                  <MemberCard key={m.id} member={m} onEdit={openEdit} onDelete={setDeleteTarget} canManage={canManage} isOwner={false} />
                ))
            }
          </Box>
        )}

        {/* Create / Invite / Edit dialog */}
        <Dialog open={!!dialog} onClose={closeDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {dialog === "edit" ? "Edit Member" : dialog === "invite" ? "Invite by Email" : "Add Member"}
          </DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 3, pb: 2, overflow: "visible" }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <TextField label="First Name" fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.first_name}
                onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
              <TextField label="Last Name" fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.last_name}
                onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
            </Box>
            <TextField label="Email" fullWidth
              disabled={dialog === "edit"}
              InputLabelProps={{ shrink: true }}
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            {dialog !== "invite" && (
              <TextField
                label={dialog === "edit" ? "New Password (leave blank to keep)" : "Password"}
                type="password" fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.password}
                onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            )}
            {dialog === "edit" && (
              <FormControlLabel
                control={<Switch checked={!!form.is_active}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />}
                label="Active"
              />
            )}
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase",
                letterSpacing: "0.05em", color: "#888", marginBottom: 10 }}>
                Permissions
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 0" }}>
                {ALL_PERMISSIONS.map(perm => (
                  <PermissionToggle key={perm.key} perm={perm}
                    checked={form.permissions.includes(perm.key)}
                    onChange={togglePerm} />
                ))}
              </div>
            </div>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}
              sx={{ background: "#1a6b9a" }}>
              {saving ? "Saving…" : dialog === "invite" ? "Send Invite" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Remove Member</DialogTitle>
          <DialogContent>
            <p style={{ margin: 0 }}>
              Remove <strong>{deleteTarget?.first_name} {deleteTarget?.last_name}</strong>?
              They will lose all access immediately.
            </p>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleDelete}>Remove</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
}
